/**
 * The browser button: code that runs on a shop's product page, reads the
 * product data the page already shows (JSON-LD, Open Graph) and opens
 * Closetly with it. The data never passes through the shop, so shops that
 * refuse the server still work. It comes in two wrappers: a bookmark
 * (bookmarkletCode) and an iOS shortcut (shortcutCode).
 *
 * `collect` runs on the shop's page, not in Closetly: it may only use what
 * it declares itself.
 */
function collect(origin: string): string {
  const meta: Record<string, string> = {};
  document.querySelectorAll('meta[content]').forEach((m) => {
    const key = (m.getAttribute('property') || m.getAttribute('name') || m.getAttribute('itemprop') || '').toLowerCase();
    if (key && !(key in meta) && /^(og:|product:|twitter:|price|brand|color)/.test(key))
      meta[key] = (m.getAttribute('content') || '').slice(0, 2000);
  });
  const jsonld = Array.from(document.querySelectorAll('script[type="application/ld+json"]'), (s) => s.textContent || '')
    .filter((t) => t.includes('Product'))
    .slice(0, 3)
    .map((t) => t.slice(0, 100000));
  const link = document.querySelector('link[rel="canonical"]');
  const json = JSON.stringify({
    url: location.href,
    title: document.title.slice(0, 300),
    canonical: link ? link.getAttribute('href') || '' : '',
    meta,
    jsonld,
  });
  let bin = '';
  new TextEncoder().encode(json).forEach((b) => (bin += String.fromCharCode(b)));
  const encoded = btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return origin + '/share#page=' + encoded;
}

const call = (origin: string) => `(${collect.toString()})(${JSON.stringify(origin)})`;

/**
 * A bookmark address. Percent-encoded, so pasting it can't mangle `#`, `%`
 * or spaces; browsers decode javascript: URLs before running them.
 */
export function bookmarkletCode(origin: string): string {
  return 'javascript:' + encodeURIComponent(`location.href=${call(origin)};void 0`);
}

/**
 * For the iOS Shortcuts action "Run JavaScript on Web Page", which hands the
 * page to the script and expects the result through completion(). The
 * shortcut's next action opens the returned URL.
 */
export function shortcutCode(origin: string): string {
  return `completion(${call(origin)});`;
}
